import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';

const BRAND = 'linear-gradient(135deg,#2cbdc4,#094f6f)';

// ── Earn data ────────────────────────────────────────────────────────────────
// r = renter (students & professionals), l = landlord/property manager
const EARN_TABS = [
  {
    id: 'rent',
    label: '🏠 Paying Rent',
    headline: 'Every dollar you pay in rent earns a point.',
    sub: 'Upload your receipt weekly, fortnightly, or monthly. We verify within 24 hours.',
    rows: [
      { event: 'Verified rent payment',        note: 'Per payment — any frequency',  r: '$1 = 1 pt', l: '—'      },
      { event: 'Monthly subscription renewal', note: 'Awarded each billing cycle',   r: '250–500',   l: '1,000'  },
    ],
    visual: {
      renter:   { icon: '🏠', headline: '$400/wk rent',  sub: '= 400 pts per payment',        accent: '#2cbdc4' },
      landlord: { icon: '📅', headline: '1,000 pts',      sub: 'per renewal as property manager', accent: '#2cbdc4' },
    },
  },
  {
    id: 'referrals',
    label: '🤝 Referring Friends',
    headline: 'Refer anyone. Get paid when they do.',
    sub: 'Points land the moment your referral completes their first payment. No cap.',
    renterRows: [
      { event: 'Refer a fellow renter (student)',    note: 'On their first payment', pts: '1,000' },
      { event: 'Refer a working professional',       note: 'On their first payment', pts: '2,500' },
    ],
    landlordRows: [
      { event: 'Refer a renter (student)',           note: 'On their first payment', pts: '1,000' },
      { event: 'Refer a renter (professional)',      note: 'On their first payment', pts: '2,500' },
      { event: 'Refer another property manager',     note: 'On their first payment', pts: '5,000' },
    ],
    visual: {
      renter:   { icon: '🤝', headline: 'Refer a working professional', sub: '= 2,500 pts instantly', accent: '#10B981' },
      landlord: { icon: '🤝', headline: 'Refer another landlord',   sub: '= 5,000 pts instantly', accent: '#10B981' },
    },
  },
  {
    id: 'activity',
    label: '⭐ Platform Activity',
    headline: 'Points for being an active member.',
    sub: 'One-time bonuses for completing your profile, writing reviews, and using VibeMate.',
    rows: [
      { event: 'Signup bonus',                   note: 'Instant on account creation',       r: '500–1,000', l: '2,500' },
      { event: 'Complete your profile',          note: 'One-time',                          r: '500',       l: '500'   },
      { event: 'Complete VibeMate assessment',   note: 'One-time — renters only',           r: '375',       l: '—'     },
      { event: 'Verified property review',       note: 'Once per property',                 r: '500',       l: '500'   },
      { event: 'First listing published',        note: 'Property managers only — one-time', r: '—',         l: '2,500' },
      { event: 'Tenant placed via platform',     note: 'Per successful placement',          r: '—',         l: '3,750' },
    ],
    visual: {
      renter:   { icon: '⭐', headline: '375 pts',   sub: 'for completing VibeMate quiz', accent: '#F59E0B' },
      landlord: { icon: '🏢', headline: '2,500 pts', sub: 'on first listing published',   accent: '#F59E0B' },
    },
  },
  {
    id: 'milestones',
    label: '🏆 Milestones',
    headline: 'Reach a milestone. Get a bonus.',
    sub: 'Hit a points threshold and an automatic bonus drops into your account.',
    rows: [
      { event: 'Reach 10,000 pts',   note: 'Automatic milestone bonus',  r: '+500',   l: '+500'   },
      { event: 'Reach 20,000 pts',   note: 'Automatic milestone bonus',  r: '+1,000', l: '+1,000' },
      { event: 'Reach 50,000 pts',   note: 'Automatic milestone bonus',  r: '+2,500', l: '+2,500' },
      { event: '1-year anniversary', note: '12 consecutive months',       r: '5,000',  l: '5,000'  },
    ],
    visual: {
      renter:   { icon: '🏆', headline: '+5,000 pts', sub: 'on 1-year anniversary', accent: '#8B5CF6' },
      landlord: { icon: '🏆', headline: '+5,000 pts', sub: 'on 1-year anniversary', accent: '#8B5CF6' },
    },
  },
];

// ── Brand logo config ─────────────────────────────────────────────────────────
// type: 'icon' = Simple Icons SVG (international, confirmed 200)
// type: 'tile' = CSS brand-color tile (Australian / no icon available)
const BRAND_LOGOS = {
  coles:      { type: 'tile', color: '#E8032D', text: 'coles',     textColor: '#fff' },
  woolworths: { type: 'tile', color: '#00A651', text: 'Woolworths', textColor: '#fff' },
  ubereats:   { type: 'icon', slug: 'ubereats',   iconBg: '#06C167' },
  doordash:   { type: 'icon', slug: 'doordash',   iconBg: '#FF3008' },
  amazon:     { type: 'tile', color: '#FF9900', text: 'amazon',    textColor: '#111' },
  jbhifi:     { type: 'tile', color: '#FFE000', text: 'JB Hi-Fi',  textColor: '#111' },
  spotify:    { type: 'icon', slug: 'spotify',    iconBg: '#1DB954' },
  netflix:    { type: 'icon', slug: 'netflix',    iconBg: '#E50914' },
  uber:       { type: 'icon', slug: 'uber',       iconBg: '#000000' },
  qantas:     { type: 'icon', slug: 'qantas',     iconBg: '#E8112D' },
};

function BrandLogo({ brandKey }) {
  const b = BRAND_LOGOS[brandKey];
  if (!b) return <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 text-xs">?</div>;
  if (b.type === 'icon') return (
    <div className="w-full h-full flex items-center justify-center"
      style={{ background: b.iconBg }}>
      <img
        src={`https://cdn.simpleicons.org/${b.slug}/ffffff`}
        alt={brandKey}
        className="w-10 h-10 object-contain"
        onError={e => { e.target.style.display = 'none'; }}
      />
    </div>
  );
  return (
    <div className="w-full h-full flex items-center justify-center px-3"
      style={{ background: b.color }}>
      <span className="font-black text-lg leading-tight text-center"
        style={{ color: b.textColor }}>
        {b.text}
      </span>
    </div>
  );
}

// ── Redeem data ──────────────────────────────────────────────────────────────
const REDEEM_CATS = [
  {
    id: 'all', label: 'All rewards',
    cards: [
      { brandKey: 'coles',      name: 'Coles eGift Card',       pts: '10,000', val: '$10',    cat: 'Groceries'    },
      { brandKey: 'woolworths', name: 'Woolworths eGift Card',  pts: '10,000', val: '$10',    cat: 'Groceries'    },
      { brandKey: 'ubereats',   name: 'Uber Eats Gift Card',    pts: '10,000', val: '$10',    cat: 'Food'         },
      { brandKey: 'doordash',   name: 'DoorDash Gift Card',     pts: '10,000', val: '$10',    cat: 'Food'         },
      { brandKey: 'amazon',     name: 'Amazon AU Gift Card',    pts: '10,000', val: '$10',    cat: 'Tech'         },
      { brandKey: 'jbhifi',     name: 'JB Hi-Fi Gift Card',     pts: '10,000', val: '$10',    cat: 'Tech'         },
      { brandKey: 'spotify',    name: 'Spotify Premium',        pts: '10,000', val: '$10',    cat: 'Tech',  soon: true },
      { brandKey: 'netflix',    name: 'Netflix Credit',         pts: '10,000', val: '$10',    cat: 'Tech',  soon: true },
      { brandKey: 'uber',       name: 'Uber Gift Card',         pts: '10,000', val: '$10',    cat: 'Travel'       },
      { brandKey: 'qantas',     name: 'Qantas Points',          pts: '5,000',  val: '500 QFF',cat: 'Travel',soon: true },
      { emoji: '👑', name: '1 Month Free Sub',               pts: '10,000', val: '$10',    cat: 'Platform'     },
      { emoji: '⚡', name: 'VibeMate Plus',                 pts: '20,000', val: '$20',    cat: 'Platform'     },
      { emoji: '🚀', name: 'Featured Listing',              pts: '20,000', val: '7 days', cat: 'Platform'     },
      { emoji: '🏠', name: 'Rent Credit',                   pts: '25,000', val: '$25',    cat: 'Renter Perks',soon: true },
    ],
  },
];

const CAT_FILTERS = ['All rewards', 'Groceries', 'Food', 'Tech', 'Platform', 'Travel', 'Renter Perks'];

// ── FAQ ───────────────────────────────────────────────────────────────────────
const FAQS = [
  { q: 'Do I need to pay rent through RentalX?', a: 'No — pay your landlord however you normally would. Upload your bank receipt or screenshot to your dashboard, and we\'ll verify and award your points within 24 hours.' },
  { q: 'When do my points expire?', a: 'Points expire 12 months after earning. Any rent upload or subscription renewal extends all your existing points by 6 months. Points earned in the last 30 days are always protected.' },
  { q: 'What\'s the minimum I need to redeem?', a: 'The minimum redemption is 10,000 points = $10. 1,000 points = $1 AUD. No maximum.' },
  { q: 'Can I earn on multiple properties?', a: 'You can earn on one verified tenancy per calendar week. If you move, your new tenancy automatically becomes your earning property.' },
  { q: 'How long does receipt verification take?', a: 'Usually within 24 hours. Once approved, points appear immediately and you\'ll receive a notification.' },
  { q: 'Can I transfer points to someone else?', a: 'Points are non-transferable and tied to your account. They cannot be gifted or combined with another user\'s balance.' },
];

// ── Animated counter ─────────────────────────────────────────────────────────
function AnimatedNumber({ target, duration = 1400, suffix = '' }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const triggered = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !triggered.current) {
        triggered.current = true;
        let start = 0;
        const step = target / (duration / 16);
        const timer = setInterval(() => {
          start = Math.min(start + step, target);
          setVal(Math.round(start));
          if (start >= target) clearInterval(timer);
        }, 16);
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
}

// ─────────────────────────────────────────────────────────────────────────────
export default function RewardsExplainer() {
  const [activeMain, setActiveMain] = useState('earn');
  const [earnTab, setEarnTab]       = useState('rent');
  const [userType, setUserType]     = useState('renter');  // renter | landlord
  const [catFilter, setCatFilter]   = useState('All rewards');
  const [openFaq, setOpenFaq]       = useState(null);
  const [ptInput, setPtInput]       = useState('');
  const tabBarRef = useRef(null);
  const [isSticky, setIsSticky]     = useState(false);

  const isLandlord = userType === 'landlord';
  const currentEarn = EARN_TABS.find(t => t.id === earnTab) || EARN_TABS[0];
  const currentVisual = currentEarn.visual?.[userType] || currentEarn.visual?.renter;
  // Referral rows are user-type specific
  const currentRows = currentEarn.id === 'referrals'
    ? (isLandlord ? currentEarn.landlordRows : currentEarn.renterRows)
    : currentEarn.rows;
  const COL = isLandlord ? 'l' : 'r';
  const allCards     = REDEEM_CATS[0].cards;
  const filteredCards = catFilter === 'All rewards'
    ? allCards
    : allCards.filter(c => c.cat === catFilter);

  const ptsDollarValue = ptInput ? (parseInt(ptInput.replace(/,/g, ''), 10) / 1000).toFixed(2) : null;

  useEffect(() => {
    const handleScroll = () => {
      if (tabBarRef.current) {
        const rect = tabBarRef.current.getBoundingClientRect();
        setIsSticky(rect.top <= 0);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'inherit' }}>

      {/* ── Nav ── */}
      <div className="border-b border-white/10 py-4 px-6 absolute top-0 left-0 right-0 z-20"
        style={{ background: 'transparent' }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-0">
            <span className="text-xl font-black tracking-tight text-white">Rental</span>
            <img src="/rentalx-x-new.svg" alt="X"
              style={{ height: '1.4rem', width: 'auto', marginLeft: '0.05em', marginBottom: '0.02em', verticalAlign: 'middle', filter: 'brightness(10)' }} />
          </Link>
          <Link to="/register/student"
            className="text-sm font-bold text-white/70 hover:text-white transition-colors">
            Start earning →
          </Link>
        </div>
      </div>

      {/* ── HERO ── */}
      <section className="relative overflow-hidden pt-28 pb-20 px-4"
        style={{ background: 'linear-gradient(160deg,#041e2b 0%,#094f6f 60%,#083a54 100%)', minHeight: '520px' }}>
        {/* subtle grain */}
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")' }} />

        <div className="max-w-5xl mx-auto relative">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 text-white/70 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#2cbdc4] animate-pulse" />
            RentalX Rewards Program
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight mb-5">
            Rent is the biggest<br />
            bill you pay.<br />
            <span style={{ color: '#2cbdc4' }}>Make it work for you.</span>
          </h1>
          <p className="text-xl text-white/55 max-w-xl mb-12 leading-relaxed">
            Every dollar you pay in verified rent earns points — redeemable for gift cards, free months, and real-world rewards.
          </p>

          {/* Stat row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
            {[
              { n: 1000, suffix: ' pts', label: '= A$1 in rewards' },
              { n: 10000, suffix: ' pts', label: 'minimum redemption' },
              { n: 400, suffix: '+', label: 'brands to redeem with' },
              { n: 24, suffix: 'h', label: 'receipt verification' },
            ].map((s, i) => (
              <div key={i} className="bg-white/8 border border-white/10 rounded-2xl px-5 py-4 backdrop-blur-sm">
                <p className="text-2xl sm:text-3xl font-black text-white mb-0.5">
                  <AnimatedNumber target={s.n} suffix={s.suffix} />
                </p>
                <p className="text-xs text-white/45">{s.label}</p>
              </div>
            ))}
          </div>

          <Link to="/register/student"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl font-bold text-white hover:opacity-90 transition-all hover:scale-105"
            style={{ background: BRAND }}>
            Start earning today <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ── STICKY TAB BAR ── */}
      <div ref={tabBarRef} className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 flex gap-1 py-1">
          {[
            { id: 'earn',   label: 'How You Earn'  },
            { id: 'redeem', label: 'Redeem Points'  },
            { id: 'how',    label: 'How It Works'   },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveMain(tab.id)}
              className={`px-5 py-3 rounded-xl text-sm font-bold transition-all ${
                activeMain === tab.id
                  ? 'text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
              style={activeMain === tab.id ? { background: BRAND } : {}}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* EARN SECTION                                           */}
      {/* ═══════════════════════════════════════════════════════ */}
      {activeMain === 'earn' && (
        <section className="py-16 px-4">
          <div className="max-w-5xl mx-auto">

            {/* User type toggle — Renter vs Property Manager */}
            <div className="flex justify-center mb-10">
              <div className="inline-flex bg-gray-100 rounded-2xl p-1 gap-1">
                {[
                  { id: 'renter',   label: '🏠 Renter' },
                  { id: 'landlord', label: '🏢 Property Manager' },
                ].map(t => (
                  <button key={t.id} onClick={() => setUserType(t.id)}
                    className={`px-7 py-2.5 rounded-xl text-sm font-bold transition-all ${
                      userType === t.id ? 'text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                    style={userType === t.id ? { background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' } : {}}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

              {/* Left: vertical tabs */}
              <div className="lg:col-span-4 flex flex-row lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
                {EARN_TABS.map(tab => (
                  <button key={tab.id} onClick={() => setEarnTab(tab.id)}
                    className={`flex-shrink-0 flex items-start gap-3 px-4 py-3 rounded-2xl text-left transition-all border ${
                      earnTab === tab.id
                        ? 'border-[#094f6f]/30 bg-[#094f6f]/5'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}>
                    <div>
                      <p className={`text-sm font-bold whitespace-nowrap ${earnTab === tab.id ? 'text-[#094f6f]' : 'text-gray-700'}`}>
                        {tab.label}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5 hidden lg:block">{tab.sub.slice(0, 48)}…</p>
                    </div>
                  </button>
                ))}
              </div>

              {/* Right: content */}
              <div className="lg:col-span-8">
                <div className="mb-6">
                  <h2 className="text-2xl font-black text-gray-900 mb-2">{currentEarn.headline}</h2>
                  <p className="text-gray-500 text-sm leading-relaxed">{currentEarn.sub}</p>
                </div>

                {/* Visual highlight card */}
                <div className="rounded-2xl p-6 mb-6 flex items-center gap-4"
                  style={{ background: `${currentVisual.accent}12`, border: `1.5px solid ${currentVisual.accent}30` }}>
                  <span className="text-4xl flex-shrink-0">{currentVisual.icon}</span>
                  <div>
                    <p className="text-2xl font-black" style={{ color: currentVisual.accent }}>{currentVisual.headline}</p>
                    <p className="text-sm text-gray-500">{currentVisual.sub}</p>
                  </div>
                </div>

                {/* Earnings table */}
                <div className="rounded-2xl border border-gray-200 overflow-hidden">
                  {currentEarn.id === 'referrals' ? (
                    // Referral rows are pre-filtered per user type — no column header
                    <>
                      <div className="grid grid-cols-2 bg-gray-50 border-b border-gray-200 px-5 py-2.5 text-xs font-bold uppercase tracking-wide text-gray-500">
                        <div>Who you refer</div>
                        <div className="text-right">Points earned</div>
                      </div>
                      {currentRows.map((row, i) => (
                        <div key={i} className="grid grid-cols-2 items-center px-5 py-3.5 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
                          <div>
                            <p className="text-sm font-medium text-gray-800">{row.event}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{row.note}</p>
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-black text-[#094f6f]">{row.pts}</span>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <>
                      <div className="grid grid-cols-3 bg-gray-50 border-b border-gray-200 px-5 py-2.5 text-xs font-bold uppercase tracking-wide text-gray-500">
                        <div className="col-span-2">Action</div>
                        <div className="text-right">Points</div>
                      </div>
                      {currentRows.map((row, i) => (
                        <div key={i} className="grid grid-cols-3 items-center px-5 py-3.5 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
                          <div className="col-span-2">
                            <p className="text-sm font-medium text-gray-800">{row.event}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{row.note}</p>
                          </div>
                          <div className="text-right">
                            <span className={`text-sm font-black ${row[COL] === '—' ? 'text-gray-300' : 'text-[#094f6f]'}`}>
                              {row[COL]}
                            </span>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* REDEEM SECTION                                         */}
      {/* ═══════════════════════════════════════════════════════ */}
      {activeMain === 'redeem' && (
        <section className="py-16 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-3">Turn points into rewards</h2>
              <p className="text-gray-500 max-w-xl mx-auto">1,000 points = A$1. Minimum 10,000 pts. Instant or within 24 hours.</p>
            </div>

            {/* Points converter */}
            <div className="rounded-3xl mb-10 p-6 sm:p-8"
              style={{ background: 'linear-gradient(160deg,#041e2b,#094f6f)' }}>
              <p className="text-white/60 text-xs font-bold uppercase tracking-widest mb-4">Points Calculator</p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex items-center gap-3 flex-1">
                  <input
                    type="text"
                    placeholder="Enter your points"
                    value={ptInput}
                    onChange={e => {
                      const raw = e.target.value.replace(/[^\d]/g, '');
                      setPtInput(raw ? parseInt(raw, 10).toLocaleString() : '');
                    }}
                    className="flex-1 bg-white/10 border border-white/20 text-white placeholder:text-white/30 rounded-xl px-4 py-3 text-lg font-bold focus:outline-none focus:border-[#2cbdc4] transition-colors"
                  />
                  <span className="text-white/50 text-sm font-medium whitespace-nowrap">pts =</span>
                </div>
                <div className="bg-white/10 border border-white/15 rounded-xl px-6 py-3 flex-shrink-0">
                  <p className="text-3xl font-black text-white">
                    {ptsDollarValue ? `A$${ptsDollarValue}` : 'A$0.00'}
                  </p>
                  <p className="text-white/40 text-xs mt-0.5">in reward value</p>
                </div>
              </div>
              {ptInput && parseFloat(ptInput.replace(/,/g, '')) < 10000 && (
                <p className="text-amber-400 text-xs mt-3">Minimum 10,000 pts required to redeem · You need {(10000 - parseInt(ptInput.replace(/,/g, ''), 10)).toLocaleString()} more pts</p>
              )}
              {ptInput && parseFloat(ptInput.replace(/,/g, '')) >= 10000 && (
                <p className="text-[#2cbdc4] text-xs mt-3">✓ You can redeem — explore your options below</p>
              )}
            </div>

            {/* Category filter pills */}
            <div className="flex flex-wrap gap-2 mb-8">
              {CAT_FILTERS.map(cat => (
                <button key={cat} onClick={() => setCatFilter(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                    catFilter === cat
                      ? 'text-white border-transparent shadow-sm'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  style={catFilter === cat ? { background: BRAND } : {}}>
                  {cat}
                </button>
              ))}
            </div>

            {/* Reward card grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredCards.map((card, i) => (
                <div key={i}
                  className={`group relative bg-white rounded-2xl border flex flex-col transition-all hover:shadow-lg hover:-translate-y-0.5 overflow-hidden ${
                    card.soon ? 'opacity-60 border-gray-200' : 'border-gray-200 hover:border-[#094f6f]/30'
                  }`}>
                  {/* Logo area */}
                  <div className="h-24 w-full overflow-hidden flex-shrink-0 rounded-t-2xl">
                    {card.brandKey ? (
                      <BrandLogo brandKey={card.brandKey} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-4xl bg-gray-50">
                        {card.emoji}
                      </div>
                    )}
                  </div>
                  {/* Info */}
                  <div className="px-4 py-3 flex flex-col gap-1 flex-1">
                    <p className="text-sm font-bold text-gray-900 leading-tight">{card.name}</p>
                    <p className="text-xs text-gray-400">{card.cat}</p>
                    <div className="mt-auto pt-2 border-t border-gray-100 flex items-center justify-between">
                      <span className="text-xs font-black text-[#094f6f]">{card.pts} pts</span>
                      <span className="text-xs text-gray-500 font-semibold">{card.val}</span>
                    </div>
                  </div>
                  {card.soon && (
                    <span className="absolute top-2 right-2 text-[9px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">Soon</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* HOW IT WORKS                                           */}
      {/* ═══════════════════════════════════════════════════════ */}
      {activeMain === 'how' && (
        <section className="py-16 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-3">Up and earning in 3 steps</h2>
              <p className="text-gray-500">No credit card. No complicated rules. Just pay your rent.</p>
            </div>

            {/* Steps */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
              {[
                { n: '01', icon: '✍️', title: 'Sign up & get your bonus',   desc: 'Create your free RentalX account and receive an instant welcome bonus — up to 2,500 points before you\'ve paid a single dollar in rent.' },
                { n: '02', icon: '🏠', title: 'Pay rent, upload your receipt', desc: 'Pay your landlord however you normally would. Upload the bank receipt in your dashboard — we verify it within 24 hours, every time.' },
                { n: '03', icon: '🎁', title: 'Redeem for real rewards',      desc: 'Once verified, points hit your wallet instantly. Redeem for Coles, JB Hi-Fi, IKEA, and 400+ brands — or use them for a free subscription month.' },
              ].map(s => (
                <div key={s.n} className="relative bg-white rounded-3xl border border-gray-200 p-7 shadow-sm">
                  <div className="flex items-start gap-4 mb-5">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-black text-lg flex-shrink-0"
                      style={{ background: BRAND }}>
                      {s.n}
                    </div>
                    <span className="text-3xl mt-0.5">{s.icon}</span>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{s.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>

            {/* Frequency breakdown */}
            <div className="rounded-3xl p-8 mb-16" style={{ background: '#f8fafc', border: '1.5px solid #e5e7eb' }}>
              <h3 className="text-xl font-black text-gray-900 mb-6 text-center">Same rate, any payment frequency</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {[
                  { icon: '📅', label: 'Weekly', eg: '$400/wk → 400 pts' },
                  { icon: '📆', label: 'Fortnightly', eg: '$900/fn → 900 pts' },
                  { icon: '🗓️', label: 'Monthly', eg: '$1,800/mo → 1,800 pts' },
                ].map(f => (
                  <div key={f.label} className="bg-white rounded-2xl p-5 text-center shadow-sm border border-gray-100">
                    <span className="text-3xl block mb-2">{f.icon}</span>
                    <p className="font-bold text-gray-900 mb-1">{f.label}</p>
                    <p className="text-2xl font-black text-[#094f6f] my-2">$1 = 1 pt</p>
                    <p className="text-xs text-[#2cbdc4] font-semibold">{f.eg}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div>
              <h3 className="text-2xl font-black text-gray-900 mb-6 text-center">Common questions</h3>
              <div className="space-y-2">
                {FAQS.map((f, i) => (
                  <div key={i} className="border border-gray-200 rounded-2xl overflow-hidden">
                    <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors">
                      <span className="font-semibold text-gray-900 text-sm pr-4">{f.q}</span>
                      {openFaq === i ? <ChevronUp className="w-4 h-4 text-[#2cbdc4] flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                    </button>
                    {openFaq === i && (
                      <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-3">{f.a}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="mt-16 text-center rounded-3xl py-14 px-6" style={{ background: 'linear-gradient(160deg,#041e2b,#094f6f)' }}>
              <h2 className="text-3xl font-black text-white mb-3">Start earning from day one.</h2>
              <p className="text-white/50 mb-8">Your signup bonus is waiting.</p>
              <Link to="/founding"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl font-bold text-white hover:opacity-90 transition-all hover:scale-105"
                style={{ background: BRAND }}>
                Join as a founding member <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
